import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './Sheet.module.scss';

/*
 * גיליון תחתון — התרגום ל-React של js/components/sheet.js בנדרים.
 *
 * במסך צר הוא מחליף את תפריט הפעולות הצף של השורה. הוא **לא** מחליף
 * את CustomMenu בדסקטופ: שם יש מקום לתפריט צף, והוא נשאר כמו שהוא.
 */

export interface SheetItem {
  key: string;
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  disabled?: boolean;
  /** פעולה הרסנית (ביטול בקשה) — נבדלת בצבע ובמשקל. */
  danger?: boolean;
}

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  items: SheetItem[];
  emptyMessage?: string;
}

const SLIDE_MS = 300;

const Sheet = ({ open, onClose, title, items, emptyMessage }: SheetProps) => {
  /*
   * ⚠️ שני מצבים ולא אחד. `mounted` מחזיק את הגיליון ב-DOM לאורך
   * אנימציית היציאה, ו-`shown` מריץ את ההחלקה. בלי ההפרדה הגיליון
   * היה נעלם מיידית בסגירה — האנימציה רצה על צומת שכבר הוסר.
   */
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<Element | null>(null);

  useEffect(() => {
    if (open) {
      returnFocusRef.current = document.activeElement;
      setMounted(true);
      // פריים אחד אחרי ההרכבה, אחרת הדפדפן מחשב את שני המצבים יחד
      // ואין מה להנפיש.
      const frame = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(frame);
    }

    setShown(false);
    const timer = setTimeout(() => {
      setMounted(false);
      // מחזיר את המיקוד לכפתור שפתח, אחרת הוא נופל ל-body והמקלדת
      // מתחילה מראש הדף.
      if (returnFocusRef.current instanceof HTMLElement) returnFocusRef.current.focus();
    }, SLIDE_MS);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    // גלילת הרקע ננעלת, אחרת גרירה בתוך הגיליון מזיזה את הדף שמאחוריו.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (shown) sheetRef.current?.querySelector<HTMLElement>('button:not(:disabled)')?.focus();
  }, [shown]);

  if (!mounted) return null;

  return createPortal(
    <>
      <div className={styles.scrim} onClick={onClose} role="presentation" />
      <div
        ref={sheetRef}
        className={`${styles.sheet} ${shown ? '' : styles.hidden}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className={styles.grab} aria-hidden="true" />
        {title && <div className={styles.title}>{title}</div>}
        <div className={styles.list}>
          {items.length === 0 && emptyMessage && <div className={styles.empty}>{emptyMessage}</div>}
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`${styles.item} ${item.danger ? styles.danger : ''}`}
              disabled={item.disabled}
              onClick={() => {
                // נסגר לפני שהפעולה רצה: פעולה שפותחת דיאלוג משלה
                // הייתה נפתחת מתחת לגיליון שעדיין על המסך.
                onClose();
                item.onClick();
              }}
            >
              {item.icon && <span className={styles.itemIcon}>{item.icon}</span>}
              <span className={styles.itemName}>{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    </>,
    document.body
  );
};

export default Sheet;
